import { IButtonStyles, IProcessedStyleSet, Stack, mergeStyles } from '@fluentui/react';
import React from 'react';
import { useSelector } from 'react-redux';
import { AlchemySolutionModel, BcqrExperimentType, ContentType, UserInfo } from '../../../web-legacy/models/apiModels';
import { AutomationIds } from '../../../web-legacy/models/constants/automationIds';
import { Environment } from '../../../web-legacy/models/constants/constants';
import { IAppState } from '../../../web-legacy/models/reduxState';
import {
  isGetHelpSelector,
  isPostAlchemyFeedbackEnabledSelector,
  isSignedInSelector,
  isThirdPartySelector,
  marketSelector,
  userInfoSelector,
} from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import { getLinkStyles } from '../../../web-legacy/packages/contactUs/styles/commonStyles';
import {
  IContactUsStyles,
  getContactUsClassNames,
} from '../../../web-legacy/packages/contactUs/styles/contactUsStyles';
import { IStyledComponent } from '../../../web-legacy/packages/contactUs/styles/themesUI';
import { StyledText } from '../../common/components/StyledText';
import { appContext } from '../../models/getHelp/appContext';
import { EmeraldUserType } from '../../models/getHelp/emeraldUserType';
import { LogLevel, TagId } from '../../models/getHelp/GetHelpLoggingRequest';
import { decodeHtmlEntities } from '../../utils/getHelputils';
import { appendArticleUrl, getIsControl, removeBcqrSolutionTitleSuffix } from '../../utils/selfHelpUtils';
import { ContentStyle, RecommendedArticleStackStyle, ResultTitleStyle } from '../styles/selfHelpStyles';
import { LinkWithContextMenu } from './LinkWithContextMenu';
import { Tag } from './Tag';

interface BcqrResultV2Props {
  alchemyResult: AlchemySolutionModel;
  index: number;
  bcqrExperimentType: BcqrExperimentType;
  correlationId: string;
  isDifferentContentType: boolean;
  isInApp: boolean;
  contentType: ContentType;
  // eslint-disable-next-line @typescript-eslint/ban-types
  articleOnClickHandler: (url: string) => {};
}

type IBcqrResultV2Props = BcqrResultV2Props & IStyledComponent<IContactUsStyles>;

export const BcqrResultV2 = (props: IBcqrResultV2Props) => {
  const runningInSoc: boolean = useSelector((state: IAppState) => state.GlobalState.environment === Environment.SOC);
  const isThirdParty: boolean = useSelector(isThirdPartySelector);
  const market: string = useSelector(marketSelector);
  const isSignedIn: boolean = useSelector(isSignedInSelector);
  const userInfo: UserInfo = useSelector(userInfoSelector);
  const isGetHelp: boolean = useSelector(isGetHelpSelector);
  const isControl: boolean = getIsControl(props.bcqrExperimentType);
  const isPostAlchemyFeedbackEnabled = useSelector(isPostAlchemyFeedbackEnabledSelector);
  const userEmail: string = userInfo ? userInfo.MemberName : null;
  const contactUsClassNames: IProcessedStyleSet<IContactUsStyles> = getContactUsClassNames(props.styles, {
    theme: props.theme,
  });
  const linkStyles: IButtonStyles = getLinkStyles();

  let articleUrl = appendArticleUrl(
    props.alchemyResult.URI,
    props.correlationId,
    isControl,
    isSignedIn,
    userEmail,
    props.contentType,
    market,
    isGetHelp
  );
  const shouldShowBanners: boolean = !isControl && props.isDifferentContentType;
  const dataBiProps = {
    'data-bi-bhvr': 'SHOW',
    'data-bi-area': 'selfHelpAlchemyBCQR',
    'data-bi-contenttype': props.alchemyResult.ContentType,
    'data-bi-formnm': 'maxFunnel',
    'data-bi-name': props.alchemyResult.Title,
    'data-bi-srchlinkpos': props.index,
    'data-bi-iscontactsuggestedselfhelp': 'true',
    'data-bi-contentid': props.alchemyResult.Source + props.index,
  };
  let articleOnClickHandler = null;
  // to be used by ContextMenu when we set articleUrl = undefined
  const contextUrl: string = `${articleUrl}`;
  let title: string = props.alchemyResult.Title || '';
  if (runningInSoc || isThirdParty || props.isInApp || isGetHelp) {
    title = !isControl ? removeBcqrSolutionTitleSuffix(props.alchemyResult.Title, market) : props.alchemyResult.Title;
    title = title || '';
  } else {
    articleOnClickHandler = () => props.articleOnClickHandler(contextUrl);
    articleUrl = 'javascript:void(0)';
  }

  if (isGetHelp) {
    dataBiProps['data-bi-message'] = 'RecommendedArticle:Read the article';
    dataBiProps['data-bi-locale'] = appContext.locale ?? null;
    dataBiProps['data-bi-sessionid'] = appContext.sessionId ?? null;
    dataBiProps['data-bi-title'] = title ?? null;
    dataBiProps['data-bi-EmeraldUserType'] = EmeraldUserType[appContext.emeraldUserType] ?? null;
  }

  const detailText = decodeHtmlEntities(props.alchemyResult.Description);
  return (
    <Stack className={isGetHelp ? mergeStyles(RecommendedArticleStackStyle.root) : contactUsClassNames.bodyCopySmall}>
      <LinkWithContextMenu
        className={
          isGetHelp ? mergeStyles(ResultTitleStyle.root) : mergeStyles(linkStyles, contactUsClassNames.articleLink)
        }
        url={articleUrl}
        // Todo: Brook verify why gethelp was splitting title
        text={decodeHtmlEntities(title.split(' - ')[0])}
        sendAlchemyFeedback={isPostAlchemyFeedbackEnabled}
        dataBiProps={dataBiProps}
        onClick={articleOnClickHandler}
        contextUrl={contextUrl}
        eventMessage={{
          TagId: TagId.ClickedBcqrReadArticle,
          LogLevel: LogLevel.Info,
          Message: 'Clicked BCQR Read article',
          Adhoc0: appContext.locale,
          Adhoc1: articleUrl,
          Adhoc2: title,
          Adhoc3: ContentType[props.contentType],
          Adhoc4: String(props.index + 1),
          Adhoc5: EmeraldUserType[appContext.emeraldUserType],
        }}
        id={"ms-BCQRStackLink-" + props.index.toString()}
        role="link"
        aria-label={title}
        tabIndex={0}
        sendAlchemyGPTFeedback={false}
      />
      <Tag useTag={shouldShowBanners} contentType={props.alchemyResult.ContentType} theme={props.theme} />
      <StyledText
        className={
          isGetHelp
            ? mergeStyles(ContentStyle.root)
            : mergeStyles(
              contactUsClassNames.articleContentShortened,
              contactUsClassNames.searchSnippet,
              ContentStyle.root
            )
        }
        data-automation-id={AutomationIds.SolutionsDescription}
        tabIndex={0}
        aria-label={detailText}
        aria-describedby={"ms-BCQRStackLink-" + props.index.toString()}
        role="article"
        id={"ms-BCQRStackDescription-" + props.index.toString()}
        data-is-focusable={true}
      >
        {detailText}
      </StyledText>
    </Stack>
  );
};
